import { useCallback, useEffect, useState } from 'react';
import api from '../api/client';
import Modal from './Modal';
import MarkdownText from './MarkdownText';

const POLL_MS = 20000;

/**
 * Кнопка «Быстрые» на странице задачи во время урока.
 * Вопросы — в модалке (presence не меняется); тренажёр — новая вкладка.
 */
export default function LessonQuickItemsStudent({ lessonSessionId = null }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [answerDraft, setAnswerDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [startingTrainer, setStartingTrainer] = useState(false);

  const load = useCallback(async () => {
    if (!lessonSessionId) {
      setItems([]);
      return;
    }
    try {
      const res = await api.get(`/tasks/lesson-sessions/${lessonSessionId}/quick-items/`);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch {
      // тихо: урок мог закончиться
    }
  }, [lessonSessionId]);

  useEffect(() => {
    void load();
    if (!lessonSessionId) return undefined;
    const id = window.setInterval(() => {
      void load();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [lessonSessionId, load]);

  useEffect(() => {
    if (!open) return;
    void load();
  }, [open, load]);

  if (!lessonSessionId) return null;

  const unansweredPrompts = items.filter((it) => it.kind === 'prompt' && !it.answered);
  const badgeCount = unansweredPrompts.length + items.filter((it) => it.kind === 'trainer').length;
  if (items.length === 0) return null;

  const active = items.find((it) => it.id === activeId) || null;

  async function submitAnswer() {
    if (!active || active.kind !== 'prompt' || submitting) return;
    const answer = String(answerDraft || '').trim();
    if (!answer) {
      setError('Выберите или введите ответ.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await api.post(
        `/tasks/lesson-sessions/${lessonSessionId}/quick-items/${active.id}/answer/`,
        { answer },
      );
      const updated = res.data;
      setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
      setActiveId(updated.id);
      setAnswerDraft('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось отправить ответ.');
    } finally {
      setSubmitting(false);
    }
  }

  async function openTrainer(item) {
    if (!item?.trainer_assignment_id || startingTrainer) return;
    if (item.latest_session_id) {
      window.open(`/trainers/sessions/${item.latest_session_id}`, '_blank', 'noopener,noreferrer');
      return;
    }
    setStartingTrainer(true);
    setError('');
    try {
      const res = await api.post(`/trainers/assignments/${item.trainer_assignment_id}/start/`);
      const sid = res.data?.id;
      if (sid) {
        window.open(`/trainers/sessions/${sid}`, '_blank', 'noopener,noreferrer');
        await load();
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось открыть тренажёр.');
    } finally {
      setStartingTrainer(false);
    }
  }

  function openItem(item) {
    setError('');
    setActiveId(item.id);
    setAnswerDraft(item.answered ? (item.student_answer || '') : '');
  }

  return (
    <>
      <button
        type="button"
        className="btn btn-sm btn-outline lesson-quick-student-btn"
        onClick={() => {
          setOpen(true);
          setActiveId(null);
          setError('');
        }}
        title="Короткие задания, пока преподаватель занят"
      >
        Быстрые
        {unansweredPrompts.length > 0 ? (
          <span className="lesson-quick-student-badge">{unansweredPrompts.length}</span>
        ) : badgeCount > 0 ? (
          <span className="lesson-quick-student-badge lesson-quick-student-badge--muted">{items.length}</span>
        ) : null}
      </button>

      <Modal
        open={open}
        title="Быстрые задания"
        onClose={() => {
          if (submitting) return;
          setOpen(false);
          setActiveId(null);
        }}
      >
        {error ? <div className="alert alert-error">{error}</div> : null}

        {!active ? (
          <ul className="lesson-quick-student-list">
            {items.map((item) => (
              <li key={item.id} className="lesson-quick-student-row">
                {item.kind === 'trainer' ? (
                  <>
                    <span className="lesson-journal-hw-kind lesson-journal-hw-kind--trainer">Тренажёр</span>
                    <span className="lesson-quick-student-title">{item.title || 'Тренажёр'}</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      disabled={startingTrainer}
                      onClick={() => void openTrainer(item)}
                    >
                      {startingTrainer ? '…' : 'Открыть'}
                    </button>
                  </>
                ) : (
                  <>
                    <span className="lesson-journal-hw-kind lesson-journal-hw-kind--quiz">Вопрос</span>
                    <span className="lesson-quick-student-title">{item.prompt || 'Вопрос'}</span>
                    {item.answered ? (
                      <span className={`text-secondary${item.is_correct ? ' is-ok' : ' is-bad'}`}>
                        {item.is_correct ? 'верно' : 'неверно'}
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => openItem(item)}
                      >
                        Ответить
                      </button>
                    )}
                    {item.answered ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        onClick={() => openItem(item)}
                      >
                        Смотреть
                      </button>
                    ) : null}
                  </>
                )}
              </li>
            ))}
          </ul>
        ) : active.kind === 'prompt' ? (
          <div className="lesson-quick-student-prompt">
            <button
              type="button"
              className="btn btn-xs btn-outline"
              onClick={() => {
                setActiveId(null);
                setAnswerDraft('');
                setError('');
              }}
            >
              ← К списку
            </button>
            <div className="lesson-quick-student-prompt-text">
              <MarkdownText text={active.prompt || ''} />
            </div>
            {active.answered ? (
              <div className="lesson-quick-student-result">
                <p className={active.is_correct ? 'work-answer-hint-ok' : 'work-answer-hint-wrong'}>
                  {active.is_correct ? 'Верно' : 'Неверно'}
                </p>
                <p className="text-secondary">
                  Ваш ответ: {active.student_answer || '—'}
                </p>
                {active.expected_answer != null && active.expected_answer !== '' ? (
                  <p className="text-secondary">
                    Правильный ответ: {active.expected_answer}
                    {active.answer_kind === 'single_choice' && Array.isArray(active.options)
                      ? (() => {
                        const opt = active.options.find(
                          (o) => String(o.id) === String(active.expected_answer),
                        );
                        return opt?.text ? ` — ${opt.text}` : '';
                      })()
                      : ''}
                  </p>
                ) : null}
              </div>
            ) : active.answer_kind === 'single_choice' ? (
              <ul className="choice-answer-options" role="radiogroup">
                {(active.options || []).map((opt) => {
                  const checked = String(answerDraft) === String(opt.id);
                  return (
                    <li key={opt.id} className={`choice-answer-option${checked ? ' is-selected' : ''}`}>
                      <label className="choice-answer-option-label">
                        <input
                          type="radio"
                          name={`quick-${active.id}`}
                          checked={checked}
                          disabled={submitting}
                          onChange={() => setAnswerDraft(String(opt.id))}
                        />
                        <span className="choice-answer-option-id">{opt.id}.</span>
                        <span className="choice-answer-option-text">
                          <MarkdownText text={opt.text || ''} className="choice-answer-option-md" />
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <label className="form-label" style={{ display: 'block' }}>
                Ответ
                <input
                  type="text"
                  className="form-input"
                  value={answerDraft}
                  onChange={(e) => setAnswerDraft(e.target.value)}
                  disabled={submitting}
                  autoFocus
                />
              </label>
            )}
            {!active.answered ? (
              <div style={{ marginTop: '0.75rem' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={submitting || !String(answerDraft || '').trim()}
                  onClick={() => void submitAnswer()}
                >
                  {submitting ? '…' : 'Отправить'}
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </>
  );
}
